// Process-singleton CpV2Client for the cp-org-console FE.
//
// Per LD-JWT in
// `intelliauth-backend/docs/superpowers/specs/2026-05-09-cp-org-console-api-gap-fill.md`
// the cp-org-console attaches a JWT Bearer access token on every
// authenticated request. This module owns:
//
//   - the in-memory token store (lifetime = browser tab; no localStorage,
//     no cookies)
//   - typed wrappers around `/api/v1/auth/login`, `/auth/logout`, and
//     `/auth/session` (Phase 4 Wave 1 endpoints)
//   - the 401 hook that clears the token + bounces to `/login` when an
//     authenticated call comes back unauthorized
//
// All FE code goes through `cpClient` rather than newing up its own
// CpV2Client — consistent token state across the tab.

import { CpV2ApiError, CpV2Client } from '@intelliauth/api-client-v2-cp'
import type { components } from '@intelliauth/api-client-v2-cp/types'

// Re-export the typed DTOs the FE wants — drives type-safe AuthContext +
// LoginPage + dashboard chrome without each callsite re-importing
// `components['schemas']['Foo']`.
export type LoginRequest = components['schemas']['LoginRequest']
export type LoginResponse = components['schemas']['LoginResponse']
export type SessionResponse = components['schemas']['SessionResponse']
export type User = components['schemas']['User']
export type OrgMembership = components['schemas']['OrgMembership']

// The singleton is created lazily so the unauthorized hook can be
// re-bound after AuthContext mounts. `cpClient.setUnauthorizedHandler`
// (added below) lets AuthContext install its own logout + redirect on
// 401 without rebuilding the whole client.
let _cpClient: CpV2Client | null = null
let _onUnauthorized: (() => void) | null = null

// Token storage — STRICT MODULE-SCOPE MEMORY for BOTH tokens.
//
// Final storage contract:
//   - access_token  → owned by CpV2Client.accessToken (private field
//                     inside the api-client-v2-cp module). Not exposed
//                     by getter; only setAccessToken / clearAccessToken
//                     write it.
//   - refresh_token → module variable in this file. Never persisted.
//   - NO sessionStorage / localStorage / cookies for either token.
//
// Trade-off, eyes open:
//   - Hard reload (F5 / tab restore / close+reopen) loses BOTH tokens.
//     The next API call has no Authorization header → 401 → bounce to
//     /login. Login is cheap.
//   - sessionStorage is reachable from JS by definition; an XSS payload
//     can `sessionStorage.getItem('ia.cp.access_token')` in one line.
//     Module-scope vars (no export accessor) and class private fields
//     aren't reachable that way — the only way to read them is to
//     import the module, which an XSS payload generally can't do
//     (it runs in the evaluator's scope, not the bundler's).
//   - No HttpOnly cookie either: the user specifically said to keep
//     tokens out of the browser-visible store entirely, even though
//     HttpOnly hides them from JS. This is the strictest mode.
let _refreshToken: string | null = null

// _refreshInFlight de-duplicates concurrent refresh attempts. When
// 4 dashboard widgets fan out and all hit 401 simultaneously, we want
// ONE refresh call, not four — otherwise they'd race against each
// other to consume the same refresh_token (which Aegis rotates
// atomically; four parallel refreshes would all-but-one trip the
// "token_reuse" compromise alarm).
let _refreshInFlight: Promise<string | null> | null = null

// _authGeneration is bumped on every explicit auth-state transition
// (login, logout, explicit clear). A refresh-in-flight captures the
// generation when it starts and DISCARDS its result if the generation
// changed mid-flight. This closes the logout-during-refresh race:
// without it, a user clicking "Sign out" while a refresh is in flight
// could have their session "un-cleared" by the still-pending refresh
// promise resolving and setting fresh tokens.
let _authGeneration = 0

// Anonymous endpoint allowlist. Match against the un-interpolated path
// template that CpV2Client passes to onUnauthorized (e.g.
// "/api/v1/invitations/{token}", not the URL with values substituted).
//
// Source of truth: docs/superpowers/specs/2026-05-09-cp-org-console-api-gap-fill.md
// + intelliauth-backend/cp/cmd/cp-api/router.go (every route registered
// WITHOUT authmw.RequireAuthenticated / RequireOrgScope / RequireScope).
// Keep this in sync with the backend's public-route wiring; the cp-api
// boot fails closed if a scope-less route is registered, so this list
// is a positive enumeration of the explicitly-public surface.
const ANONYMOUS_PATHS: ReadonlySet<string> = new Set([
  '/api/v1/signup',
  '/api/v1/availability/email',
  '/api/v1/availability/org-slug',
  '/api/v1/auth/login',
  '/api/v1/auth/logout',
  // /auth/session is DELIBERATELY NOT here. Mid-session, the access
  // token can expire while the FE is otherwise idle; the next
  // /auth/session call (e.g. AuthContext focus-refetch) gets 401 and
  // we want the refresh-interceptor to silently mint fresh tokens
  // from the cookie + retry — exactly like every other privileged
  // call. The "first-time visitor with no cookie" case is handled
  // upstream by tryRestoreSession() on boot, which determines
  // logged-in state BEFORE checkSession even runs.
  // Deliberately NOT including /api/v1/auth/refresh — a 401 from
  // /auth/refresh means the refresh_token itself is dead (Aegis
  // returned token_reuse / invalid_token / session_inactive). The
  // user genuinely has no valid session and SHOULD be redirected to
  // /login. The refresh helper bypasses CpV2Client anyway (raw
  // fetch + manual error handling), so this is belt-and-braces for
  // future call sites.
  '/api/v1/auth/recover/request',
  '/api/v1/auth/recover/reset',
  '/api/v1/auth/verify/request',
  '/api/v1/auth/verify/confirm',
  '/api/v1/auth/mfa/challenge',
  '/api/v1/auth/mfa/options',
  '/api/v1/auth/mfa/enroll/totp/start',
  '/api/v1/auth/mfa/enroll/totp/confirm',
  '/api/v1/auth/mfa/enroll/sms/start',
  '/api/v1/auth/mfa/enroll/sms/confirm',
  '/api/v1/auth/mfa/enroll/webauthn/start',
  '/api/v1/auth/mfa/enroll/webauthn/confirm',
  '/api/v1/invitations/{token}',
  '/api/v1/invitations/{token}/accept',
  '/api/v1/tenant-admin-invitations/{token}',
  '/api/v1/tenant-admin-invitations/{token}/accept',
])

function isAnonymousPath(path: string): boolean {
  return ANONYMOUS_PATHS.has(path)
}

// clearAuthState resets BOTH tokens and bumps the generation so any
// refresh-in-flight discards its result. Always call this on logout,
// on terminal /auth/refresh failure, and on explicit "force logout."
function clearAuthState(): void {
  _refreshToken = null
  _authGeneration += 1
  _cpClient?.clearAccessToken()
}

/**
 * Same-origin baseUrl. The FE is served behind Oathkeeper at
 * `manage.<DOMAIN_BASE>` and Oathkeeper routes `/api/v1/*` to cp-api.
 * Empty string means relative URLs → browser hits the same origin →
 * Oathkeeper → cp-api. No CORS, no cross-host JWT leakage.
 */
const BASE_URL = ''

/** Returns the lazily-built singleton. */
export function cpClient(): CpV2Client {
  if (!_cpClient) {
    _cpClient = new CpV2Client({
      baseUrl: BASE_URL,
      // The client now drives the full refresh-and-retry cycle inside
      // a single request() call. onUnauthorized only fires AFTER:
      //   (a) anonymous-path 401 (never refresh) — handler suppresses
      //   (b) refresh callback unavailable or returned null
      //   (c) the retried request still 401'd (revoked or rotated)
      // In all those cases the session is genuinely dead and we route
      // the user to /login.
      onUnauthorized: ({ path }) => {
        if (isAnonymousPath(path)) return
        clearAuthState()
        _onUnauthorized?.()
      },
      isAnonymousPath,
      // refreshAccessToken returns the new access token (string) or
      // null. Concurrent 401 callers share the in-flight promise (see
      // _refreshInFlight) so Aegis only sees one /sessions/refresh
      // call per token batch — critical because Aegis treats a second
      // use of the same refresh_token as a token_reuse compromise.
      refreshAccessToken: () => refreshAccessToken(),
    })
    // NOTE: no sessionStorage rehydration. Both tokens are module-
    // scope memory only — a hard reload always lands the user at
    // /login. Trade-off accepted: login is cheap and keeping tokens
    // out of browser storage closes off devtools-copy + XSS-getItem
    // attacks.
  }
  return _cpClient
}

/**
 * Register the global 401 handler — called once from AuthContext on
 * mount. Lets the context redirect to `/login` (or surface a "session
 * expired" toast) without coupling the API package to react-router.
 */
export function setUnauthorizedHandler(fn: () => void): void {
  _onUnauthorized = fn
}

/**
 * Set the in-memory tokens after a successful auth-state transition
 * (login or refresh). Bumps the generation counter so any
 * concurrently in-flight refresh that started before this call
 * discards its result.
 *
 * NOTE: NOT exported. The only legal callers are login() and the
 * internal refresh path. Keeping this private removes the attack
 * surface where an XSS payload importing `setTokens` could mint
 * tokens out of band. (Test files would have to monkey-patch the
 * module to reach it; production code can't.)
 */
function setTokens(access: string, refresh: string | null): void {
  // refresh might be null only on edge cases (Aegis returned empty);
  // never store an empty string masquerading as a token.
  _refreshToken = refresh && refresh.length > 0 ? refresh : null
  _authGeneration += 1
  _cpClient?.setAccessToken(access)
}

/**
 * POST /api/v1/auth/refresh.
 *
 * Atomically rotates: the presented refresh_token is consumed by
 * Aegis and a new pair (access_token + refresh_token) is returned.
 *
 * Concurrent callers (e.g. four dashboard widgets all hit 401 at
 * once) MUST share one in-flight refresh — otherwise four parallel
 * requests would race to consume the same refresh_token, and Aegis
 * would reject all-but-one as token_reuse (compromise alarm). The
 * _refreshInFlight Promise serializes this.
 *
 * Returns the new access_token on success, or null on failure. On
 * failure the in-memory state is cleared so the next call routes to
 * /login.
 *
 * Three subtle correctness guards baked in:
 *
 *   1. Generation capture: if logout() bumps the generation while a
 *      refresh is in flight, the resolver discards the new tokens
 *      (the user clicked Sign Out — don't revive their session).
 *
 *   2. Shape validation: an empty / malformed `data.access_token`
 *      from Aegis treats refresh as failed instead of writing
 *      "Bearer undefined" into the client.
 *
 *   3. Promise sharing: the in-flight promise is returned to every
 *      concurrent caller; no `await Promise.race` rejoin race.
 */
async function refreshAccessToken(): Promise<string | null> {
  if (_refreshInFlight) return _refreshInFlight
  const startGeneration = _authGeneration
  _refreshInFlight = (async () => {
    try {
      const rt = _refreshToken
      // Two source-of-credential paths:
      //   1. In-memory refresh token → send in body. Used by the
      //      mid-session "access token expired" interceptor path.
      //   2. No in-memory refresh token → POST with EMPTY body. The
      //      backend falls back to the ia_cp_refresh HttpOnly cookie
      //      (which the browser auto-replays on same-origin
      //      requests). This is the page-reload session-restore
      //      path — JS memory was wiped but the cookie survived.
      // If neither yields a valid session, the backend returns 401
      // and we route the user to /login.
      const init: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }
      if (rt) {
        init.body = JSON.stringify({ refresh_token: rt })
      }
      const res = await fetch('/api/v1/auth/refresh', init)
      // Guard 1: did the user log out / re-login while refresh was
      // in flight? If so, throw the rotated tokens on the floor.
      if (_authGeneration !== startGeneration) return null
      if (!res.ok) {
        clearAuthState()
        return null
      }
      const json = (await res.json()) as
        | { data?: { access_token?: unknown; refresh_token?: unknown } }
        | null
      const access = json?.data?.access_token
      const refresh = json?.data?.refresh_token
      // Guard 2: validate shape before writing.
      if (typeof access !== 'string' || access.length === 0) {
        clearAuthState()
        return null
      }
      // Race re-check: another logout might have happened between the
      // fetch and the json parse. Re-check generation.
      if (_authGeneration !== startGeneration) return null
      setTokens(access, typeof refresh === 'string' ? refresh : null)
      return access
    } catch {
      clearAuthState()
      return null
    } finally {
      _refreshInFlight = null
    }
  })()
  return _refreshInFlight
}

/**
 * Page-reload session restore. Tries the HttpOnly refresh cookie
 * (which survives reload) to mint a fresh access-token pair without
 * making the user re-authenticate. Returns true if the session was
 * restored, false otherwise.
 *
 * Call ONCE on app mount, BEFORE any other authenticated request.
 * After it resolves, the in-memory tokens are either populated (the
 * cookie was valid) or empty (no cookie, or Aegis rejected it) — in
 * either case the next call to getSession() will do the right thing.
 */
export async function tryRestoreSession(): Promise<boolean> {
  // IMPORTANT: force CpV2Client lazy-init BEFORE the refresh
  // resolves. setTokens(access, refresh) wants to call
  // _cpClient.setAccessToken(...); if _cpClient is still null at that
  // moment the access token quietly never attaches to subsequent
  // requests and the session-restore appears to fail. Touch
  // cpClient() once here to materialize the singleton.
  cpClient()
  const tok = await refreshAccessToken()
  return tok !== null
}

// Note: an older attemptRefreshOrRedirect() helper used to live here.
// It's gone — the same logic now lives INSIDE CpV2Client.request()
// (refresh-and-retry interceptor pattern), so a single user-initiated
// request transparently handles an expired access token without the
// caller ever seeing the 401. The cpClient.onUnauthorized handler
// above only fires when refresh fails or the path is anonymous.

// === Auth flows ===

/**
 * POST /api/v1/auth/login.
 *
 * On success, stores the access token on the singleton client so all
 * subsequent calls attach `Authorization: Bearer <token>`. Returns the
 * full LoginResponse so callers can render the user / orgs immediately
 * without a follow-up Session call.
 *
 * Throws a CpV2ApiError on 4xx/5xx — callers branch on `status` /
 * `code` to render field-level errors (422), unauthorized banners
 * (401), or service-unavailable retries (503).
 */
export async function login(req: LoginRequest): Promise<LoginResponse> {
  const c = cpClient()
  const env = await c.post('/api/v1/auth/login', { body: req })
  // Envelope is `{data: LoginResponse, meta}`. Unwrap.
  const data = (env as { data: LoginResponse }).data
  if (data?.access_token) {
    // Both tokens go through setTokens → module-scope memory only.
    // Bumps generation; CpV2Client gets the new access token via the
    // setter so subsequent calls attach `Authorization: Bearer …`.
    setTokens(data.access_token, data.refresh_token ?? null)
  }
  // Defensive: zap any Set-Cookie cp-api might inadvertently send.
  // We don't want tokens visible in document.cookie regardless of
  // HttpOnly status.
  void clearAnyAuthCookies()
  return data
}

/**
 * POST /api/v1/auth/logout.
 *
 * Clears the in-memory token regardless of the server response —
 * the FE-side intent is "I want to be logged out" and the cp-api's
 * 204 only confirms the server side. Network/server errors are
 * swallowed (logged) so the user's "Sign out" click never gets
 * stuck.
 */
export async function logout(): Promise<void> {
  const c = cpClient()
  try {
    await c.post('/api/v1/auth/logout')
  } catch (err) {
    console.warn('[auth] logout backend call failed; clearing token anyway', err)
  } finally {
    clearAuthState()
    void clearAnyAuthCookies()
  }
}

// clearAnyAuthCookies scrubs ANY browser-visible cookies that look
// like they could carry auth state. cp-api doesn't currently set any
// (login returns JSON only — checked via curl -i), but defense-in-
// depth: if a future middleware or proxy starts emitting a cookie we
// don't want it sticking around. The theme cookie (ia_theme_hint) is
// explicitly preserved.
function clearAnyAuthCookies(): void {
  if (typeof document === 'undefined') return
  const KEEP = new Set(['ia_theme_hint'])
  const all = document.cookie.split(';').map((c) => c.trim().split('=')[0])
  for (const name of all) {
    if (!name || KEEP.has(name)) continue
    // Expire each candidate at root path + current host + parent
    // domain. Browser is single-threaded so this is cheap.
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`
  }
}

/**
 * GET /api/v1/auth/session.
 *
 * The "who am I" call AuthContext makes on mount + after login to
 * populate the user identity + the org switcher. Throws when no token
 * is set (anonymous → 401 from cp-api → CpV2ApiError(401)).
 */
export async function getSession(): Promise<SessionResponse> {
  const c = cpClient()
  const env = await c.get('/api/v1/auth/session')
  return (env as { data: SessionResponse }).data
}

/** Convenience for callers that want to know whether the singleton has
 *  a token without importing CpV2Client directly. */
export function isAuthenticated(): boolean {
  return cpClient().hasAccessToken()
}

/** Re-export the runtime error class so callers can `instanceof`-check
 *  rejections (`err instanceof CpV2ApiError`). */
export { CpV2ApiError }
